import React, { useState, useRef, useEffect } from 'react';
import { Calendar, Clock, Video, Users, CheckCircle2, ArrowRight, Shield, Sparkles, Globe, Smartphone, Chrome, BookOpen, GraduationCap, HelpCircle, Gift, Star, ShoppingBag, Package, Shirt } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { ScheduleModal } from '../components/ScheduleModal';
import { SEO, servicesPageJsonLd, breadcrumbs, consultationFaqJsonLd } from '../components/SEO';

interface ConsultationType {
  id: string;
  title: string;
  description: string;
  duration: string;
  price: string;
  originalPrice?: string;
  features: string[];
  buttonText: string;
  scheduleUrl: string;
  recommended?: boolean;
  isDiscounted?: boolean;
  icon?: React.ElementType;
  category: 'quick' | 'strategy';
}

interface ServiceType {
  id: string;
  icon: React.ElementType;
  title: string;
  description: string;
  scheduleUrl: string;
}

const consultationTypes: ConsultationType[] = [
  // Quick Call - Entry Point
  {
    id: 'coffee-chat',
    title: 'Coffee Chat ☕',
    description: 'Quick 15-minute call to discuss your project idea or get quick advice.',
    duration: '15',
    price: '$4.99',
    originalPrice: '$9.99',
    features: [
      'Quick project discussion',
      'Initial recommendations',
      'Technology stack advice',
      'Next steps guidance',
    ],
    buttonText: 'Book Coffee Chat',
    scheduleUrl: '#',
    recommended: false,
    isDiscounted: true,
    category: 'quick',
  },
  // Strategy Sessions - Premium
  {
    id: 'allegro-strategy',
    title: 'Allegro Strategy',
    description: 'Deep-dive strategy for Allegro sellers looking to scale.',
    duration: '60',
    price: '$500',
    features: [
      'Complete account audit',
      'Listing optimization',
      'Competitor analysis',
      'Pricing & promotion tactics',
      'Growth roadmap',
      '30-day action plan',
    ],
    buttonText: 'Book Session',
    scheduleUrl: 'https://calendar.google.com/calendar/appointments/schedules/AcZssZ2GRss_OzvdFa97dK-RZi6PiFTY7g1W4pxF5-gixTkZ-NQv3b7geIhCSozzLceFbx0ykNH0tOBT?gv=true',
    recommended: true,
    isDiscounted: false,
    icon: ShoppingBag,
    category: 'strategy',
  },
  {
    id: 'amazon-strategy',
    title: 'Amazon Strategy',
    description: 'Master Amazon selling with expert guidance.',
    duration: '60',
    price: '$500',
    features: [
      'FBA/FBM optimization',
      'Listing & SEO audit',
      'PPC strategy review',
      'Buy Box tactics',
      'Inventory planning',
      '30-day action plan',
    ],
    buttonText: 'Book Session',
    scheduleUrl: 'https://calendar.google.com/calendar/appointments/schedules/AcZssZ2GRss_OzvdFa97dK-RZi6PiFTY7g1W4pxF5-gixTkZ-NQv3b7geIhCSozzLceFbx0ykNH0tOBT?gv=true',
    recommended: false,
    isDiscounted: false,
    icon: Package,
    category: 'strategy',
  },
  {
    id: 'ebay-strategy',
    title: 'eBay Strategy',
    description: 'Scale your eBay business with proven tactics.',
    duration: '60',
    price: '$500',
    features: [
      'Store optimization',
      'Listing best practices',
      'Promoted listings strategy',
      'Seller level improvement',
      'International expansion',
      '30-day action plan',
    ],
    buttonText: 'Book Session',
    scheduleUrl: 'https://calendar.google.com/calendar/appointments/schedules/AcZssZ2GRss_OzvdFa97dK-RZi6PiFTY7g1W4pxF5-gixTkZ-NQv3b7geIhCSozzLceFbx0ykNH0tOBT?gv=true',
    recommended: false,
    isDiscounted: false,
    icon: ShoppingBag,
    category: 'strategy',
  },
  {
    id: 'resale-strategy',
    title: 'Vinted/Depop Strategy',
    description: 'Grow your resale business on fashion platforms.',
    duration: '60',
    price: '$500',
    features: [
      'Profile optimization',
      'Photography & styling tips',
      'Pricing strategies',
      'Boosting & promotion',
      'Cross-platform selling',
      '30-day action plan',
    ],
    buttonText: 'Book Session',
    scheduleUrl: 'https://calendar.google.com/calendar/appointments/schedules/AcZssZ2GRss_OzvdFa97dK-RZi6PiFTY7g1W4pxF5-gixTkZ-NQv3b7geIhCSozzLceFbx0ykNH0tOBT?gv=true',
    recommended: false,
    isDiscounted: false,
    icon: Shirt,
    category: 'strategy',
  },
];

const otherServices: ServiceType[] = [
  {
    id: 'web-apps',
    icon: Globe,
    title: 'Web Applications',
    description: 'Custom SaaS and web apps built with modern, scalable technology.',
    scheduleUrl: '#'
  },
  {
    id: 'mobile-apps',
    icon: Smartphone,
    title: 'Mobile Apps',
    description: 'Native and cross-platform apps for iOS and Android.',
    scheduleUrl: '#'
  },
  {
    id: 'browser-extensions',
    icon: Chrome,
    title: 'Browser Extensions',
    description: 'Chrome extensions that solve real problems for your users.',
    scheduleUrl: '#'
  },
  {
    id: 'digital-guides',
    icon: BookOpen,
    title: 'Digital Guides',
    description: 'Practical e-books on e-commerce and tech topics.',
    scheduleUrl: '#'
  },
  {
    id: 'online-courses',
    icon: GraduationCap,
    title: 'Online Courses',
    description: 'Step-by-step courses for sellers and makers.',
    scheduleUrl: '#'
  }
];

const faqItems = [
  {
    question: "What is included in the Coffee Chat?",
    answer: "The 15-minute Coffee Chat includes discussing your project idea, understanding your goals, exploring potential solutions, and providing initial recommendations for your web development, mobile app, or SaaS project."
  },
  {
    question: "What's included in the Strategy Sessions?",
    answer: "Each 60-minute strategy session includes a complete account audit, personalized optimization recommendations, competitor analysis, actionable tactics, and a 30-day action plan tailored to your specific marketplace and goals."
  },
  {
    question: "How long does a typical project take?",
    answer: "Project timelines vary based on complexity. Simple websites take 2-4 weeks, web applications 4-12 weeks, and complex SaaS products 3-6 months. During the consultation, I provide accurate estimates based on your specific requirements."
  },
  {
    question: "Do you offer ongoing support?",
    answer: "Yes, I offer maintenance packages and ongoing support for all projects. This includes bug fixes, feature updates, performance optimization, and technical support to ensure your application runs smoothly."
  },
  {
    question: "How does the referral program work?",
    answer: "Refer a friend and you both get 20% off your next consultation. Share your unique referral link after booking, and when your friend books a session, you'll both receive a discount code."
  }
];

const FaqItem: React.FC<{ question: string; answer: string }> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (isOpen && contentRef.current) {
      setTimeout(() => {
        contentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 200);
    }
  }, [isOpen]);
  
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
      >
        <span className="font-medium pr-4">{question}</span>
        <ArrowRight className={`w-4 h-4 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
      </button>
      <div 
        ref={contentRef}
        className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-96' : 'max-h-0'}`}
      >
        <p className="p-4 pt-0 text-muted-foreground text-sm leading-relaxed">
          {answer}
        </p>
      </div>
    </div>
  );
};

export const Consultation: React.FC = () => {
  const { t } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedScheduleUrl, setSelectedScheduleUrl] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('all');

  const handleScheduleClick = (url: string) => {
    if (url === '#') return;
    setSelectedScheduleUrl(url);
    setIsModalOpen(true);
  };

  const quickCall = consultationTypes.find(c => c.category === 'quick');
  const strategySessions = consultationTypes.filter(c => c.category === 'strategy');

  return (
    <div className="pt-24 pb-20">
      <SEO 
        title="Consultation"
        description="Book a consultation with Hüseyin Cem Ateş. Expert guidance on e-commerce, web development, and digital solutions."
        canonical="/consultation"
        jsonLd={{ "@context": "https://schema.org", "@graph": [servicesPageJsonLd, breadcrumbs.consultation, consultationFaqJsonLd] }}
      />
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-muted-foreground text-xs font-medium mb-6">
            <Sparkles className="w-3 h-3" />
            {t('consultation', 'badge')}
          </div>
          <h1 className="text-4xl md:text-5xl font-semibold text-foreground mb-4">
            {t('consultation', 'title')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            {t('consultation', 'subtitle')}
          </p>
        </div>

        {/* Quick Entry Point - Coffee Chat */}
        {quickCall && (
          <div className="max-w-md mx-auto mb-16">
            <p className="text-center text-sm text-muted-foreground mb-4">👋 Not sure where to start?</p>
            <div className="relative bg-card border border-border rounded-2xl p-6 hover:border-foreground/20 transition-all">
              {quickCall.isDiscounted && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-medium animate-pulse">
                    50% OFF — Limited Time
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold mb-1">{quickCall.title}</h3>
                  <p className="text-muted-foreground text-sm">{quickCall.description}</p>
                  <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {quickCall.duration} min</span>
                    <span>
                      <span className="line-through opacity-60 mr-1">{quickCall.originalPrice}</span>
                      <span className="font-semibold text-foreground">{quickCall.price}</span>
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleScheduleClick(quickCall.scheduleUrl)}
                  disabled={quickCall.scheduleUrl === '#'}
                  className="shrink-0 bg-foreground text-background px-4 py-2.5 rounded-lg font-medium hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {quickCall.scheduleUrl === '#' ? 'Soon' : 'Book'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Strategy Sessions Header */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-semibold mb-2">1-on-1 Strategy Sessions</h2>
          <p className="text-muted-foreground">Deep-dive consultations for serious sellers ready to scale</p>
        </div>

        {/* Filter Tabs */}
        <div className="flex justify-center gap-2 mb-8">
          {['all', 'allegro', 'amazon', 'ebay', 'resale'].map((filter) => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter as any)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeFilter === filter || (filter === 'all' && activeFilter === 'all')
                  ? 'bg-foreground text-background'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {filter === 'all' ? 'All' : filter === 'resale' ? 'Vinted/Depop' : filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}
        </div>

        {/* Strategy Sessions Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-20">
          {(activeFilter === 'all' ? strategySessions : strategySessions.filter(s => {
            if (activeFilter === 'allegro') return s.id === 'allegro-strategy';
            if (activeFilter === 'amazon') return s.id === 'amazon-strategy';
            if (activeFilter === 'ebay') return s.id === 'ebay-strategy';
            if (activeFilter === 'resale') return s.id === 'resale-strategy';
            return true;
          })).map((type) => {
            const IconComponent = type.icon || ShoppingBag;
            return (
              <div
                key={type.id}
                className={`relative bg-card border rounded-2xl p-6 hover:border-foreground/20 transition-all ${
                  type.recommended ? 'border-primary/50 ring-1 ring-primary/20' : 'border-border'
                }`}
              >
                {type.recommended && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                      <Star className="w-3 h-3" /> Most Popular
                    </span>
                  </div>
                )}

                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center shrink-0">
                    <IconComponent className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">{type.title}</h3>
                    <p className="text-muted-foreground text-sm">{type.description}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 mb-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    <span>{type.duration} min</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Video className="w-4 h-4" />
                    <span>Google Meet</span>
                  </div>
                </div>

                <div className="mb-4">
                  <span className="text-3xl font-semibold">{type.price}</span>
                  <span className="text-muted-foreground text-sm ml-2">one-time</span>
                </div>

                <ul className="mb-6 space-y-2">
                  {type.features.slice(0, 4).map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      <span className="text-sm text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                  {type.features.length > 4 && (
                    <li className="text-sm text-muted-foreground pl-6">
                      +{type.features.length - 4} more benefits
                    </li>
                  )}
                </ul>

                <button
                  onClick={() => handleScheduleClick(type.scheduleUrl)}
                  disabled={type.scheduleUrl === '#'}
                  className={`w-full py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                    type.recommended 
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                      : 'bg-foreground text-background hover:opacity-90'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <span>{type.scheduleUrl === '#' ? 'Coming Soon' : type.buttonText}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Case Studies Section */}
        <div className="mb-20">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-semibold mb-2">Client Success Stories</h2>
            <p className="text-muted-foreground">Real results from sellers who invested in strategy</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                platform: 'Allegro',
                client: 'Fashion Reseller',
                before: '€2,500/mo',
                after: '€12,000/mo',
                increase: '380%',
                timeline: '3 months',
                quote: 'The listing optimization alone doubled my visibility. Best investment I made.',
              },
              {
                platform: 'Amazon',
                client: 'Electronics Seller',
                before: '$8,000/mo',
                after: '$45,000/mo',
                increase: '462%',
                timeline: '6 months',
                quote: 'PPC strategy and Buy Box tactics were game-changers for our margins.',
              },
              {
                platform: 'eBay',
                client: 'Vintage Collector',
                before: '£1,200/mo',
                after: '£8,500/mo',
                increase: '608%',
                timeline: '4 months',
                quote: 'Finally broke through to Top Rated Plus. International sales exploded.',
              },
            ].map((study, index) => (
              <div key={index} className="bg-card border border-border rounded-2xl p-6 hover:border-foreground/20 transition-all">
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-2 py-1 bg-muted rounded text-xs font-medium">{study.platform}</span>
                  <span className="text-xs text-muted-foreground">{study.client}</span>
                </div>
                
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-1">Before</p>
                    <p className="text-lg font-medium text-muted-foreground">{study.before}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-primary" />
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-1">After</p>
                    <p className="text-lg font-semibold text-foreground">{study.after}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-2 py-1 bg-green-500/10 text-green-600 dark:text-green-400 rounded text-sm font-semibold">
                    +{study.increase}
                  </span>
                  <span className="text-xs text-muted-foreground">in {study.timeline}</span>
                </div>
                
                <p className="text-sm text-muted-foreground italic">"{study.quote}"</p>
              </div>
            ))}
          </div>
        </div>

        {/* Referral Program Section */}
        <div className="mb-20 bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-2xl p-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center shrink-0">
              <Gift className="w-8 h-8 text-primary" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-xl font-semibold mb-2">Refer a Friend, Get 20% Off</h3>
              <p className="text-muted-foreground">
                Know someone who could use expert guidance? Refer them and you'll both get 20% off your next consultation. 
                Win-win! 🎉
              </p>
            </div>
            <button
              className="shrink-0 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-all"
            >
              Get Referral Link
            </button>
          </div>
        </div>

        {/* Other Services Section */}
        <div className="mb-20">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider text-center mb-8">
            Other Services I Offer
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {otherServices.map((service) => {
              const IconComponent = service.icon;
              return (
                <div
                  key={service.id}
                  className="relative bg-card border border-border rounded-xl p-6 hover:border-foreground/20 transition-all group"
                >
                  <div className="mb-4">
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mb-4">
                      <IconComponent className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <h3 className="font-semibold mb-2">{service.title}</h3>
                    <p className="text-muted-foreground text-sm">{service.description}</p>
                  </div>

                  <button
                    onClick={() => handleScheduleClick(service.scheduleUrl)}
                    disabled={service.scheduleUrl === '#'}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:opacity-70 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>{service.scheduleUrl === '#' ? 'Coming Soon' : 'Schedule Call'}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* What to Expect */}
        <div className="mt-16">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider text-center mb-8">
            {t('consultation', 'expectTitle')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-card border border-border rounded-xl p-6 text-center">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Video className="w-4 h-4 text-muted-foreground" />
              </div>
              <h3 className="font-medium mb-2">{t('consultation', 'videoTitle')}</h3>
              <p className="text-sm text-muted-foreground">{t('consultation', 'videoDesc')}</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-6 text-center">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-4 h-4 text-muted-foreground" />
              </div>
              <h3 className="font-medium mb-2">{t('consultation', 'flexibleTitle')}</h3>
              <p className="text-sm text-muted-foreground">{t('consultation', 'flexibleDesc')}</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-6 text-center">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Users className="w-4 h-4 text-muted-foreground" />
              </div>
              <h3 className="font-medium mb-2">{t('consultation', 'personalTitle')}</h3>
              <p className="text-sm text-muted-foreground">{t('consultation', 'personalDesc')}</p>
            </div>
          </div>
        </div>

        {/* Trust Signals */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            <span>{t('consultation', 'trustSecure')}</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            <span>{t('consultation', 'trustGuarantee')}</span>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="max-w-3xl mx-auto mt-20 px-6">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-muted mb-4">
            <HelpCircle className="w-5 h-5 text-muted-foreground" />
          </div>
          <h2 className="text-2xl font-semibold mb-2">Frequently Asked Questions</h2>
          <p className="text-muted-foreground">Everything you need to know about working with me</p>
        </div>

        <div className="space-y-3">
          {faqItems.map((faq, index) => (
            <FaqItem key={index} question={faq.question} answer={faq.answer} />
          ))}
        </div>
      </div>

      <ScheduleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        scheduleUrl={selectedScheduleUrl}
      />
    </div>
  );
};